import React, { useState, useEffect } from 'react'
import { Plus, Minus, Send, User, MapPin, LogOut, Settings } from 'lucide-react'
import { MenuItem, OrderItem, Category } from '../types'
import { supabase } from '../lib/supabase'
import { RESTAURANT_SETTINGS_ID } from '../constants'
import HandheldTableManagement from './HandheldTableManagement'
import { Staff } from '../types'

interface HandheldProps {
  currentStaff: Staff
  onLogout: () => void
}

interface RestaurantSettings {
  has_kds: boolean
  has_kiosk: boolean
  has_handheld: boolean
}

const Handheld: React.FC<HandheldProps> = ({ currentStaff, onLogout }) => {
  const [currentView, setCurrentView] = useState<'ordering' | 'tables'>('ordering')
  const [settings, setSettings] = useState<RestaurantSettings | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [cart, setCart] = useState<OrderItem[]>([])
  const [tableNumber, setTableNumber] = useState<string>('')
  const [customerName, setCustomerName] = useState<string>('')
  const [specialRequests, setSpecialRequests] = useState<string>('')
  const [orderType, setOrderType] = useState<'dine_in' | 'takeaway' | 'collection'>('dine_in')
  const [showMealCustomization, setShowMealCustomization] = useState(false)
  const [mealCustomization, setMealCustomization] = useState<{
    menuItem: MenuItem
    isMeal: boolean
    selectedSide?: string
    selectedDrink?: string
  } | null>(null)

  useEffect(() => {
    fetchSettings()
    fetchCategories()
    fetchMenuItems()
    
    // Listen for storage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'restaurant_settings' && e.newValue) {
        try {
          const newSettings = JSON.parse(e.newValue)
          setSettings(newSettings)
        } catch (error) {
          console.error('Error parsing settings from storage:', error)
        }
      }
    }
    
    window.addEventListener('storage', handleStorageChange)
    
    // Set up real-time settings subscription
    const settingsSubscription = supabase
      .channel('restaurant_settings')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'restaurant_settings' }, () => {
        fetchSettings()
      })
      .subscribe()

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      settingsSubscription.unsubscribe()
    }
  }, [])

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('restaurant_settings')
        .select('has_kds, has_kiosk, has_handheld')
        .eq('id', RESTAURANT_SETTINGS_ID)
        .maybeSingle()

      if (error) {
        console.error('Error fetching settings:', error)
        setSettings({ has_kds: true, has_kiosk: true, has_handheld: true })
      } else {
        setSettings(data || { has_kds: true, has_kiosk: true, has_handheld: true })
      }
    } catch (error) {
      console.error('Error:', error)
      setSettings({ has_kds: true, has_kiosk: true, has_handheld: true })
    }
  }

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('display_order')

    if (error) {
      console.error('Error fetching categories:', error)
      // Mock data for demo
      setCategories([
        { id: '1', name: 'Starters', display_order: 1, created_at: new Date().toISOString() },
        { id: '2', name: 'Salads', display_order: 2, created_at: new Date().toISOString() },
        { id: '3', name: 'Soups', display_order: 3, created_at: new Date().toISOString() },
        { id: '4', name: 'Pizza', display_order: 4, created_at: new Date().toISOString() },
        { id: '5', name: 'Pasta', display_order: 5, created_at: new Date().toISOString() },
        { id: '6', name: 'Burgers', display_order: 6, created_at: new Date().toISOString() },
        { id: '7', name: 'Steaks', display_order: 7, created_at: new Date().toISOString() },
        { id: '8', name: 'Seafood', display_order: 8, created_at: new Date().toISOString() },
        { id: '9', name: 'Chicken', display_order: 9, created_at: new Date().toISOString() },
        { id: '10', name: 'Vegetarian', display_order: 10, created_at: new Date().toISOString() },
        { id: '11', name: 'Sides', display_order: 11, created_at: new Date().toISOString() },
        { id: '12', name: 'Desserts', display_order: 12, created_at: new Date().toISOString() },
        { id: '13', name: 'Hot Drinks', display_order: 13, created_at: new Date().toISOString() },
        { id: '14', name: 'Cold Drinks', display_order: 14, created_at: new Date().toISOString() },
        { id: '15', name: 'Alcohol', display_order: 15, created_at: new Date().toISOString() },
        { id: '16', name: 'Kids Menu', display_order: 16, created_at: new Date().toISOString() },
      ])
    } else {
      setCategories(data || [])
    }
  }

  const fetchMenuItems = async () => {
    const { data, error } = await supabase
      .from('menu_items')
      .select(`
        *,
        categories!menu_items_category_id_fkey (
          id,
          name
        )
      `)
      .eq('available', true)

    if (error) {
      console.error('Error fetching menu items:', error)
      // Mock data for demo
      setMenuItems([
        // Starters (Category 1)
        { id: '1', name: 'Garlic Bread', description: 'Toasted artisan bread with roasted garlic butter and herbs', price: 6.99, category: '1', available: true, preparation_time: 8, created_at: new Date().toISOString() },
        { id: '2', name: 'Chicken Wings', description: 'Buffalo wings with blue cheese dip and celery sticks', price: 9.99, category: '1', available: true, preparation_time: 12, created_at: new Date().toISOString() },
        { id: '3', name: 'Mozzarella Sticks', description: 'Crispy breaded mozzarella with marinara sauce', price: 8.99, category: '1', available: true, preparation_time: 10, created_at: new Date().toISOString() },
        { id: '4', name: 'Calamari Rings', description: 'Golden fried squid rings with spicy aioli', price: 11.99, category: '1', available: true, preparation_time: 8, created_at: new Date().toISOString() },
        { id: '5', name: 'Nachos Supreme', description: 'Loaded nachos with cheese, jalapeños, sour cream and guacamole', price: 12.99, category: '1', available: true, preparation_time: 10, created_at: new Date().toISOString() },
        { id: '6', name: 'Bruschetta', description: 'Toasted bread with fresh tomatoes, basil and balsamic glaze', price: 7.99, category: '1', available: true, preparation_time: 6, created_at: new Date().toISOString() },
        { id: '7', name: 'Prawn Cocktail', description: 'Classic prawn cocktail with Marie Rose sauce', price: 9.99, category: '1', available: true, preparation_time: 5, created_at: new Date().toISOString() },
        { id: '8', name: 'Stuffed Mushrooms', description: 'Button mushrooms stuffed with herbs and breadcrumbs', price: 8.99, category: '1', available: true, preparation_time: 15, created_at: new Date().toISOString() },

        // Salads (Category 2)
        { id: '9', name: 'Caesar Salad', description: 'Crisp romaine lettuce with parmesan, croutons and Caesar dressing', price: 12.99, category: '2', available: true, preparation_time: 8, created_at: new Date().toISOString() },
        { id: '10', name: 'Greek Salad', description: 'Mixed greens with feta, olives, tomatoes and Mediterranean dressing', price: 11.99, category: '2', available: true, preparation_time: 6, created_at: new Date().toISOString() },
        { id: '11', name: 'Chicken Cobb Salad', description: 'Grilled chicken, bacon, blue cheese, avocado and mixed greens', price: 15.99, category: '2', available: true, preparation_time: 10, created_at: new Date().toISOString() },
        { id: '12', name: 'Quinoa Power Bowl', description: 'Quinoa, roasted vegetables, chickpeas and tahini dressing', price: 13.99, category: '2', available: true, preparation_time: 8, created_at: new Date().toISOString() },
        { id: '13', name: 'Caprese Salad', description: 'Fresh mozzarella, tomatoes, basil with balsamic reduction', price: 10.99, category: '2', available: true, preparation_time: 5, created_at: new Date().toISOString() },
        { id: '14', name: 'Waldorf Salad', description: 'Apples, celery, walnuts, grapes with creamy dressing', price: 9.99, category: '2', available: true, preparation_time: 6, created_at: new Date().toISOString() },

        // Soups (Category 3)
        { id: '15', name: 'Tomato Basil Soup', description: 'Creamy tomato soup with fresh basil and croutons', price: 7.99, category: '3', available: true, preparation_time: 5, created_at: new Date().toISOString() },
        { id: '16', name: 'Chicken Noodle Soup', description: 'Homestyle chicken soup with vegetables and egg noodles', price: 8.99, category: '3', available: true, preparation_time: 8, created_at: new Date().toISOString() },
        { id: '17', name: 'French Onion Soup', description: 'Classic onion soup with gruyere cheese and croutons', price: 9.99, category: '3', available: true, preparation_time: 10, created_at: new Date().toISOString() },
        { id: '18', name: 'Mushroom Soup', description: 'Creamy wild mushroom soup with herbs', price: 8.99, category: '3', available: true, preparation_time: 8, created_at: new Date().toISOString() },
        { id: '19', name: 'Minestrone Soup', description: 'Italian vegetable soup with beans and pasta', price: 7.99, category: '3', available: true, preparation_time: 6, created_at: new Date().toISOString() },

        // Pizza (Category 4)
        { id: '20', name: 'Margherita Pizza', description: 'Classic pizza with San Marzano tomatoes, fresh mozzarella and basil', price: 16.99, category: '4', available: true, preparation_time: 15, created_at: new Date().toISOString() },
        { id: '21', name: 'Pepperoni Pizza', description: 'Traditional pepperoni with mozzarella cheese', price: 18.99, category: '4', available: true, preparation_time: 15, created_at: new Date().toISOString() },
        { id: '22', name: 'Meat Lovers Pizza', description: 'Pepperoni, sausage, bacon, ham and ground beef', price: 22.99, category: '4', available: true, preparation_time: 18, created_at: new Date().toISOString() },
        { id: '23', name: 'Vegetarian Pizza', description: 'Bell peppers, mushrooms, onions, olives and tomatoes', price: 19.99, category: '4', available: true, preparation_time: 15, created_at: new Date().toISOString() },
        { id: '24', name: 'BBQ Chicken Pizza', description: 'BBQ sauce, grilled chicken, red onions and cilantro', price: 20.99, category: '4', available: true, preparation_time: 16, created_at: new Date().toISOString() },
        { id: '25', name: 'Hawaiian Pizza', description: 'Ham, pineapple and mozzarella cheese', price: 19.99, category: '4', available: true, preparation_time: 15, created_at: new Date().toISOString() },
        { id: '26', name: 'Four Cheese Pizza', description: 'Mozzarella, parmesan, gorgonzola and ricotta', price: 21.99, category: '4', available: true, preparation_time: 15, created_at: new Date().toISOString() },
        { id: '27', name: 'Supreme Pizza', description: 'Pepperoni, sausage, peppers, onions, mushrooms and olives', price: 23.99, category: '4', available: true, preparation_time: 18, created_at: new Date().toISOString() },

        // Pasta (Category 5)
        { id: '28', name: 'Spaghetti Carbonara', description: 'Classic carbonara with pancetta, eggs, parmesan and black pepper', price: 17.99, category: '5', available: true, preparation_time: 12, created_at: new Date().toISOString() },
        { id: '29', name: 'Fettuccine Alfredo', description: 'Creamy alfredo sauce with parmesan cheese', price: 16.99, category: '5', available: true, preparation_time: 10, created_at: new Date().toISOString() },
        { id: '30', name: 'Penne Arrabbiata', description: 'Spicy tomato sauce with garlic, chili and herbs', price: 15.99, category: '5', available: true, preparation_time: 10, created_at: new Date().toISOString() },
        { id: '31', name: 'Lasagna Bolognese', description: 'Traditional meat lasagna with bechamel and parmesan', price: 19.99, category: '5', available: true, preparation_time: 25, created_at: new Date().toISOString() },
        { id: '32', name: 'Seafood Linguine', description: 'Linguine with prawns, mussels and clams in white wine sauce', price: 24.99, category: '5', available: true, preparation_time: 15, created_at: new Date().toISOString() },
        { id: '33', name: 'Chicken Pesto Pasta', description: 'Penne with grilled chicken, basil pesto and sun-dried tomatoes', price: 18.99, category: '5', available: true, preparation_time: 12, created_at: new Date().toISOString() },
        { id: '34', name: 'Mushroom Risotto', description: 'Creamy arborio rice with wild mushrooms and parmesan', price: 17.99, category: '5', available: true, preparation_time: 20, created_at: new Date().toISOString() },

        // Burgers (Category 6)
        { id: '35', name: 'Classic Beef Burger', description: 'Beef patty with lettuce, tomato, onion and our special sauce', price: 14.99, category: '6', available: true, preparation_time: 12, created_at: new Date().toISOString() },
        { id: '36', name: 'Bacon Cheeseburger', description: 'Beef patty with bacon, cheese, lettuce and tomato', price: 16.99, category: '6', available: true, preparation_time: 12, created_at: new Date().toISOString() },
        { id: '37', name: 'Mushroom Swiss Burger', description: 'Beef patty with sautéed mushrooms and Swiss cheese', price: 15.99, category: '6', available: true, preparation_time: 14, created_at: new Date().toISOString() },
        { id: '38', name: 'Chicken Burger', description: 'Grilled chicken breast with avocado and chipotle mayo', price: 15.99, category: '6', available: true, preparation_time: 12, created_at: new Date().toISOString() },
        { id: '39', name: 'Veggie Burger', description: 'Plant-based patty with lettuce, tomato and vegan mayo', price: 13.99, category: '6', available: true, preparation_time: 10, created_at: new Date().toISOString() },
        { id: '40', name: 'BBQ Burger', description: 'Beef patty with BBQ sauce, onion rings and cheddar', price: 17.99, category: '6', available: true, preparation_time: 14, created_at: new Date().toISOString() },
        { id: '41', name: 'Fish Burger', description: 'Beer-battered fish fillet with tartar sauce and lettuce', price: 14.99, category: '6', available: true, preparation_time: 12, created_at: new Date().toISOString() },

        // Steaks (Category 7)
        { id: '42', name: 'Ribeye Steak', description: '12oz ribeye steak with garlic butter and herbs', price: 32.99, category: '7', available: true, preparation_time: 20, created_at: new Date().toISOString() },
        { id: '43', name: 'Filet Mignon', description: '8oz tender filet mignon with red wine reduction', price: 42.99, category: '7', available: true, preparation_time: 18, created_at: new Date().toISOString() },
        { id: '44', name: 'NY Strip Steak', description: '10oz New York strip with compound butter', price: 28.99, category: '7', available: true, preparation_time: 18, created_at: new Date().toISOString() },
        { id: '45', name: 'T-Bone Steak', description: '16oz T-bone steak with rosemary and thyme', price: 36.99, category: '7', available: true, preparation_time: 22, created_at: new Date().toISOString() },
        { id: '46', name: 'Sirloin Steak', description: '10oz sirloin steak with peppercorn sauce', price: 24.99, category: '7', available: true, preparation_time: 16, created_at: new Date().toISOString() },

        // Seafood (Category 8)
        { id: '47', name: 'Grilled Salmon', description: 'Atlantic salmon with lemon butter and seasonal vegetables', price: 24.99, category: '8', available: true, preparation_time: 15, created_at: new Date().toISOString() },
        { id: '48', name: 'Fish & Chips', description: 'Beer-battered cod with chips and mushy peas', price: 16.99, category: '8', available: true, preparation_time: 12, created_at: new Date().toISOString() },
        { id: '49', name: 'Shrimp Scampi', description: 'Garlic butter shrimp with white wine and herbs', price: 22.99, category: '8', available: true, preparation_time: 10, created_at: new Date().toISOString() },
        { id: '50', name: 'Lobster Tail', description: 'Grilled lobster tail with drawn butter', price: 38.99, category: '8', available: true, preparation_time: 18, created_at: new Date().toISOString() },
        { id: '51', name: 'Sea Bass', description: 'Pan-seared sea bass with Mediterranean vegetables', price: 26.99, category: '8', available: true, preparation_time: 16, created_at: new Date().toISOString() },
        { id: '52', name: 'Crab Cakes', description: 'Maryland-style crab cakes with remoulade sauce', price: 19.99, category: '8', available: true, preparation_time: 12, created_at: new Date().toISOString() },

        // Chicken (Category 9)
        { id: '53', name: 'Grilled Chicken Breast', description: 'Herb-marinated chicken breast with roasted vegetables', price: 18.99, category: '9', available: true, preparation_time: 15, created_at: new Date().toISOString() },
        { id: '54', name: 'Chicken Parmesan', description: 'Breaded chicken with marinara sauce and mozzarella', price: 20.99, category: '9', available: true, preparation_time: 18, created_at: new Date().toISOString() },
        { id: '55', name: 'BBQ Chicken', description: 'Half chicken with BBQ glaze and coleslaw', price: 17.99, category: '9', available: true, preparation_time: 20, created_at: new Date().toISOString() },
        { id: '56', name: 'Chicken Tikka Masala', description: 'Tender chicken in creamy tomato curry sauce', price: 19.99, category: '9', available: true, preparation_time: 16, created_at: new Date().toISOString() },
        { id: '57', name: 'Buffalo Chicken', description: 'Spicy buffalo chicken with blue cheese dressing', price: 16.99, category: '9', available: true, preparation_time: 14, created_at: new Date().toISOString() },
        { id: '58', name: 'Chicken Fajitas', description: 'Sizzling chicken with peppers, onions and tortillas', price: 18.99, category: '9', available: true, preparation_time: 12, created_at: new Date().toISOString() },

        // Vegetarian (Category 10)
        { id: '59', name: 'Vegetable Stir Fry', description: 'Mixed vegetables in teriyaki sauce with jasmine rice', price: 14.99, category: '10', available: true, preparation_time: 10, created_at: new Date().toISOString() },
        { id: '60', name: 'Eggplant Parmesan', description: 'Breaded eggplant with marinara and mozzarella', price: 16.99, category: '10', available: true, preparation_time: 18, created_at: new Date().toISOString() },
        { id: '61', name: 'Veggie Wrap', description: 'Grilled vegetables with hummus in a spinach tortilla', price: 12.99, category: '10', available: true, preparation_time: 8, created_at: new Date().toISOString() },
        { id: '62', name: 'Mushroom Risotto', description: 'Creamy arborio rice with wild mushrooms', price: 17.99, category: '10', available: true, preparation_time: 20, created_at: new Date().toISOString() },
        { id: '63', name: 'Vegetable Curry', description: 'Mixed vegetables in coconut curry sauce with basmati rice', price: 15.99, category: '10', available: true, preparation_time: 14, created_at: new Date().toISOString() },
        { id: '64', name: 'Caprese Sandwich', description: 'Fresh mozzarella, tomato and basil on ciabatta', price: 11.99, category: '10', available: true, preparation_time: 6, created_at: new Date().toISOString() },

        // Sides (Category 11)
        { id: '65', name: 'French Fries', description: 'Crispy golden fries with sea salt', price: 4.99, category: '11', available: true, preparation_time: 6, created_at: new Date().toISOString() },
        { id: '66', name: 'Sweet Potato Fries', description: 'Crispy sweet potato fries with chipotle mayo', price: 5.99, category: '11', available: true, preparation_time: 8, created_at: new Date().toISOString() },
        { id: '67', name: 'Onion Rings', description: 'Beer-battered onion rings with ranch dip', price: 5.99, category: '11', available: true, preparation_time: 8, created_at: new Date().toISOString() },
        { id: '68', name: 'Coleslaw', description: 'Fresh cabbage slaw with creamy dressing', price: 3.99, category: '11', available: true, preparation_time: 2, created_at: new Date().toISOString() },
        { id: '69', name: 'Mac & Cheese', description: 'Creamy macaroni and cheese with breadcrumb topping', price: 6.99, category: '11', available: true, preparation_time: 10, created_at: new Date().toISOString() },
        { id: '70', name: 'Mashed Potatoes', description: 'Creamy mashed potatoes with butter and herbs', price: 4.99, category: '11', available: true, preparation_time: 5, created_at: new Date().toISOString() },
        { id: '71', name: 'Rice Pilaf', description: 'Seasoned rice with herbs and vegetables', price: 4.99, category: '11', available: true, preparation_time: 8, created_at: new Date().toISOString() },
        { id: '72', name: 'Steamed Vegetables', description: 'Seasonal vegetables steamed to perfection', price: 5.99, category: '11', available: true, preparation_time: 6, created_at: new Date().toISOString() },

        // Desserts (Category 12)
        { id: '73', name: 'Chocolate Cake', description: 'Rich chocolate layer cake with chocolate ganache', price: 8.99, category: '12', available: true, preparation_time: 3, created_at: new Date().toISOString() },
        { id: '74', name: 'Cheesecake', description: 'New York style cheesecake with berry compote', price: 9.99, category: '12', available: true, preparation_time: 3, created_at: new Date().toISOString() },
        { id: '75', name: 'Tiramisu', description: 'Classic Italian tiramisu with coffee and mascarpone', price: 9.99, category: '12', available: true, preparation_time: 3, created_at: new Date().toISOString() },
        { id: '76', name: 'Ice Cream Sundae', description: 'Vanilla ice cream with chocolate sauce and whipped cream', price: 6.99, category: '12', available: true, preparation_time: 5, created_at: new Date().toISOString() },
        { id: '77', name: 'Apple Pie', description: 'Homemade apple pie with vanilla ice cream', price: 7.99, category: '12', available: true, preparation_time: 5, created_at: new Date().toISOString() },
        { id: '78', name: 'Crème Brûlée', description: 'Vanilla custard with caramelized sugar topping', price: 8.99, category: '12', available: true, preparation_time: 3, created_at: new Date().toISOString() },
        { id: '79', name: 'Chocolate Brownie', description: 'Warm chocolate brownie with vanilla ice cream', price: 7.99, category: '12', available: true, preparation_time: 5, created_at: new Date().toISOString() },

        // Hot Drinks (Category 13)
        { id: '80', name: 'Espresso', description: 'Rich Italian espresso shot', price: 2.99, category: '13', available: true, preparation_time: 2, created_at: new Date().toISOString() },
        { id: '81', name: 'Cappuccino', description: 'Espresso with steamed milk and foam', price: 4.99, category: '13', available: true, preparation_time: 3, created_at: new Date().toISOString() },
        { id: '82', name: 'Latte', description: 'Espresso with steamed milk and light foam', price: 4.99, category: '13', available: true, preparation_time: 3, created_at: new Date().toISOString() },
        { id: '83', name: 'Americano', description: 'Espresso with hot water', price: 3.99, category: '13', available: true, preparation_time: 2, created_at: new Date().toISOString() },
        { id: '84', name: 'Hot Chocolate', description: 'Rich hot chocolate with whipped cream', price: 4.99, category: '13', available: true, preparation_time: 3, created_at: new Date().toISOString() },
        { id: '85', name: 'English Breakfast Tea', description: 'Traditional black tea blend', price: 3.99, category: '13', available: true, preparation_time: 3, created_at: new Date().toISOString() },
        { id: '86', name: 'Earl Grey Tea', description: 'Bergamot-flavored black tea', price: 3.99, category: '13', available: true, preparation_time: 3, created_at: new Date().toISOString() },
        { id: '87', name: 'Green Tea', description: 'Antioxidant-rich green tea', price: 3.99, category: '13', available: true, preparation_time: 3, created_at: new Date().toISOString() },

        // Cold Drinks (Category 14)
        { id: '88', name: 'Coca Cola', description: 'Classic Coca Cola', price: 2.99, category: '14', available: true, preparation_time: 1, created_at: new Date().toISOString() },
        { id: '89', name: 'Orange Juice', description: 'Fresh squeezed orange juice', price: 3.99, category: '14', available: true, preparation_time: 2, created_at: new Date().toISOString() },
        { id: '90', name: 'Iced Tea', description: 'Refreshing iced tea with lemon', price: 2.99, category: '14', available: true, preparation_time: 2, created_at: new Date().toISOString() },
        { id: '91', name: 'Lemonade', description: 'Fresh homemade lemonade', price: 3.99, category: '14', available: true, preparation_time: 2, created_at: new Date().toISOString() },
        { id: '92', name: 'Sparkling Water', description: 'Premium sparkling mineral water', price: 2.99, category: '14', available: true, preparation_time: 1, created_at: new Date().toISOString() },
        { id: '93', name: 'Apple Juice', description: 'Pure apple juice', price: 3.99, category: '14', available: true, preparation_time: 1, created_at: new Date().toISOString() },
        { id: '94', name: 'Cranberry Juice', description: 'Tart cranberry juice', price: 3.99, category: '14', available: true, preparation_time: 1, created_at: new Date().toISOString() },
        { id: '95', name: 'Smoothie', description: 'Mixed berry smoothie with yogurt', price: 5.99, category: '14', available: true, preparation_time: 3, created_at: new Date().toISOString() },

        // Alcohol (Category 15)
        { id: '96', name: 'House Red Wine', description: 'Full-bodied red wine by the glass', price: 7.99, category: '15', available: true, preparation_time: 2, created_at: new Date().toISOString() },
        { id: '97', name: 'House White Wine', description: 'Crisp white wine by the glass', price: 7.99, category: '15', available: true, preparation_time: 2, created_at: new Date().toISOString() },
        { id: '98', name: 'Draft Beer', description: 'Local craft beer on tap', price: 5.99, category: '15', available: true, preparation_time: 2, created_at: new Date().toISOString() },
        { id: '99', name: 'Bottled Beer', description: 'Premium bottled beer selection', price: 4.99, category: '15', available: true, preparation_time: 1, created_at: new Date().toISOString() },
        { id: '100', name: 'Margarita', description: 'Classic margarita with lime and salt rim', price: 9.99, category: '15', available: true, preparation_time: 4, created_at: new Date().toISOString() },
        { id: '101', name: 'Mojito', description: 'Refreshing mojito with mint and lime', price: 9.99, category: '15', available: true, preparation_time: 4, created_at: new Date().toISOString() },
        { id: '102', name: 'Whiskey Sour', description: 'Classic whiskey sour with lemon', price: 8.99, category: '15', available: true, preparation_time: 3, created_at: new Date().toISOString() },
        { id: '103', name: 'Cosmopolitan', description: 'Vodka cocktail with cranberry and lime', price: 9.99, category: '15', available: true, preparation_time: 3, created_at: new Date().toISOString() },

        // Kids Menu (Category 16)
        { id: '104', name: 'Chicken Nuggets', description: 'Crispy chicken nuggets with fries', price: 8.99, category: '16', available: true, preparation_time: 8, created_at: new Date().toISOString() },
        { id: '105', name: 'Kids Mac & Cheese', description: 'Creamy macaroni and cheese', price: 7.99, category: '16', available: true, preparation_time: 6, created_at: new Date().toISOString() },
        { id: '106', name: 'Mini Pizza', description: 'Personal cheese pizza', price: 9.99, category: '16', available: true, preparation_time: 10, created_at: new Date().toISOString() },
        { id: '107', name: 'Kids Burger', description: 'Mini burger with fries', price: 9.99, category: '16', available: true, preparation_time: 10, created_at: new Date().toISOString() },
        { id: '108', name: 'Grilled Cheese', description: 'Classic grilled cheese with tomato soup', price: 6.99, category: '16', available: true, preparation_time: 6, created_at: new Date().toISOString() },
        { id: '109', name: 'Fish Fingers', description: 'Breaded fish fingers with chips', price: 8.99, category: '16', available: true, preparation_time: 8, created_at: new Date().toISOString() },
        { id: '110', name: 'Kids Ice Cream', description: 'Vanilla ice cream with chocolate sauce', price: 4.99, category: '16', available: true, preparation_time: 2, created_at: new Date().toISOString() },
      ])
      setSelectedCategory('1')
    } else {
      // Transform the data to match our interface
      const transformedItems = (data || []).map(item => ({
        ...item,
        category: item.category_id
      }))
      setMenuItems(transformedItems)
      if (categories.length > 0) {
        setSelectedCategory(categories[0].id)
      }
    }
  }

  const addToCart = (menuItem: MenuItem) => {
    // Check if item can be made into a meal
    if (menuItem.can_be_meal) {
      setMealCustomization({
        menuItem,
        isMeal: false,
        selectedSide: undefined,
        selectedDrink: undefined
      })
      setShowMealCustomization(true)
      return
    }

    const existingItem = cart.find(item => item.menu_item_id === menuItem.id)
    if (existingItem) {
      setCart(cart.map(item =>
        item.menu_item_id === menuItem.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ))
    } else {
      setCart([...cart, {
        id: Date.now().toString(),
        menu_item_id: menuItem.id,
        quantity: 1,
        menu_item: menuItem
      }])
    }
  }

  const updateQuantity = (itemId: string, change: number) => {
    setCart(cart.map(item => {
      if (item.id === itemId) {
        const newQuantity = item.quantity + change
        return newQuantity <= 0 ? null : { ...item, quantity: newQuantity }
      }
      return item
    }).filter(Boolean) as OrderItem[])
  }

  const getTotalAmount = () => {
    return cart.reduce((total, item) => {
      let itemPrice = item.menu_item?.price || 0
      if (item.is_meal && item.menu_item?.meal_price_add) {
        itemPrice += item.menu_item.meal_price_add
      }
      return total + (itemPrice * item.quantity)
    }, 0)
  }

  const sendToKitchen = async () => {
    if (cart.length === 0 || !tableNumber) return

    const hasKDS = settings?.has_kds ?? true

    const orderData = {
      table_number: parseInt(tableNumber),
      customer_name: customerName || null,
      order_type: (orderType || "dine_in") as const,
      status: (hasKDS ? 'pending' : 'completed') as const,
      total_amount: getTotalAmount(),
      payment_status: 'pending' as const,
      items: cart.map(item => ({
        menu_item_id: item.menu_item_id,
        quantity: item.quantity,
        notes: specialRequests || undefined
      }))
    }

    try {
      // Insert order first
      const { data: orderResult, error: orderError } = await supabase
        .from('orders')
        .insert([{
          table_number: orderData.table_number,
          customer_name: orderData.customer_name,
          order_type: orderData.order_type,
          status: orderData.status,
          total_amount: orderData.total_amount,
          payment_status: orderData.payment_status
        }])
        .select()
        .single()

      if (orderError) {
        console.error('Error creating order:', orderError)
        alert(hasKDS ? 'Order sent to kitchen successfully (demo mode)' : 'Order completed successfully (demo mode)')
      } else {
        // Insert order items
        const orderItems = orderData.items.map(item => ({
          order_id: orderResult.id,
          menu_item_id: item.menu_item_id,
          quantity: item.quantity,
          notes: item.notes
        }))

        const { error: itemsError } = await supabase
          .from('order_items')
          .insert(orderItems)

        if (itemsError) {
          console.error('Error creating order items:', itemsError)
        }
        
        // Auto-update table status to occupied if it's a dine-in order
        if (orderType === 'dine_in' && tableNumber) {
          await supabase
            .from('restaurant_tables')
            .update({ status: 'occupied' })
            .eq('table_number', parseInt(tableNumber))
            .then(() => console.log(`Table ${tableNumber} marked as occupied`))
            .catch((error) => console.error('Error updating table status:', error))
        }
        
        alert(hasKDS ? 'Order sent to kitchen successfully!' : 'Order completed successfully!')
      }

      // Clear form
      setCart([])
      setTableNumber('')
      setCustomerName('')
      setSpecialRequests('')
      setOrderType('dine_in')
    } catch (error) {
      console.error('Error:', error)
      
      // Auto-update table status to occupied if it's a dine-in order (demo mode)
      if (orderType === 'dine_in' && tableNumber) {
        try {
          await supabase
            .from('restaurant_tables')
            .update({ status: 'occupied' })
            .eq('table_number', parseInt(tableNumber))
          console.log(`Table ${tableNumber} marked as occupied`)
        } catch (tableError) {
          console.log(`Table ${tableNumber} marked as occupied (demo mode)`)
        }
      }
      
      alert(hasKDS ? 'Order sent to kitchen successfully (demo mode)' : 'Order completed successfully (demo mode)')
      setCart([])
      setTableNumber('')
      setCustomerName('')
      setSpecialRequests('')
    }
  }

  const filteredMenuItems = menuItems.filter(item => item.category === selectedCategory)

  // Render different views based on currentView
  if (currentView === 'tables') {
    return <HandheldTableManagement currentStaff={currentStaff} onBack={() => setCurrentView('ordering')} />
  }

  return (
    <div className="max-w-md mx-auto bg-white min-h-screen">
      {/* Header */}
      <div className="bg-purple-600 text-white p-4 rounded-b-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Handheld Ordering</h1>
            <p className="text-purple-100 text-sm">Waitstaff Interface</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentView('tables')}
              className="p-2 bg-purple-700 rounded-lg hover:bg-purple-800 transition-colors"
            >
              <Settings size={18} />
            </button>
            <button
              onClick={onLogout}
              className="p-2 bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Table & Customer Info */}
      <div className="p-4 bg-gray-50 space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Order Type</label>
          <div className="w-full p-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 font-medium">
            Dine In
          </div>
        </div>
        <div className="flex space-x-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <MapPin size={16} className="mr-1" />
              Table Number *
            </label>
            <input
              type="number"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg text-center font-bold text-lg"
              placeholder="00"
              required
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <User size={16} className="mr-1" />
              Customer Name
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg"
              placeholder="Customer name (optional)"
            />
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="p-4">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors ${
                selectedCategory === category.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Menu Items */}
      <div className="px-4 pb-24">
        <div className="space-y-3">
          {filteredMenuItems.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800">{item.name}</h3>
                  <p className="text-lg font-bold text-purple-600 mt-2">£{item.price.toFixed(2)}</p>
                </div>
              </div>
              
              {/* Quantity Controls */}
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center space-x-3">
                  {cart.find(cartItem => cartItem.menu_item_id === item.id) ? (
                    <>
                      <button
                        onClick={() => {
                          const cartItem = cart.find(ci => ci.menu_item_id === item.id)
                          if (cartItem) updateQuantity(cartItem.id, -1)
                        }}
                        className="bg-red-100 text-red-600 p-2 rounded-full hover:bg-red-200"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="w-8 text-center font-bold text-lg">
                        {cart.find(cartItem => cartItem.menu_item_id === item.id)?.quantity}
                      </span>
                      <button
                        onClick={() => {
                          const cartItem = cart.find(ci => ci.menu_item_id === item.id)
                          if (cartItem) updateQuantity(cartItem.id, 1)
                        }}
                        className="bg-green-100 text-green-600 p-2 rounded-full hover:bg-green-200"
                      >
                        <Plus size={16} />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => addToCart(item)}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 flex items-center"
                    >
                      <Plus size={16} className="mr-1" />
                      Add
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Cart Summary */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-white border-t border-gray-200 p-4 shadow-lg">
          <div className="space-y-3">
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
              <textarea
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Any special requests or notes..."
                rows={2}
              />
            </div>
            
            <div className="flex justify-between items-center">
              <div>
                <span className="text-sm text-gray-600">{cart.length} items</span>
                <div className="text-xl font-bold text-gray-800">
                  Total: £{getTotalAmount().toFixed(2)}
                </div>
              </div>
              <button
                onClick={sendToKitchen}
                disabled={!tableNumber || cart.length === 0}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
              >
                <Send size={18} className="mr-2" />
                Send to Kitchen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meal Customization Modal */}
      {showMealCustomization && mealCustomization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 mb-2">{mealCustomization.menuItem.name}</h3>
              <div className="flex space-x-3 mb-4">
                <button
                  onClick={() => setMealCustomization({...mealCustomization, isMeal: false})}
                  className={`flex-1 p-3 rounded-lg font-medium transition-colors ${
                    !mealCustomization.isMeal
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Just Item
                  <div className="text-sm">£{mealCustomization.menuItem.price.toFixed(2)}</div>
                </button>
                <button
                  onClick={() => setMealCustomization({...mealCustomization, isMeal: true})}
                  className={`flex-1 p-3 rounded-lg font-medium transition-colors ${
                    mealCustomization.isMeal
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Make it a Meal
                  <div className="text-sm">£{(mealCustomization.menuItem.price + (mealCustomization.menuItem.meal_price_add || 0)).toFixed(2)}</div>
                </button>
              </div>
            </div>

            <div className="p-6">
              {mealCustomization.isMeal && (
                <div className="space-y-4 mb-6">
                  {/* Side Selection */}
                  <div>
                    <h4 className="text-gray-800 font-medium mb-3">Choose your side:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {(mealCustomization.menuItem.meal_sides || ['Chips', 'Onion Rings', 'Sweet Potato Fries', 'Side Salad']).map(side => (
                        <button
                          key={side}
                          onClick={() => setMealCustomization({...mealCustomization, selectedSide: side})}
                          className={`p-2 rounded text-sm transition-colors ${
                            mealCustomization.selectedSide === side
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {side}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Drink Selection */}
                  <div>
                    <h4 className="text-gray-800 font-medium mb-3">Choose your drink:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {(mealCustomization.menuItem.meal_drinks || ['Coke', 'Pepsi', 'Orange Juice', 'Water']).map(drink => (
                        <button
                          key={drink}
                          onClick={() => setMealCustomization({...mealCustomization, selectedDrink: drink})}
                          className={`p-2 rounded text-sm transition-colors ${
                            mealCustomization.selectedDrink === drink
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {drink}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowMealCustomization(false)
                    setMealCustomization(null)
                  }}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!mealCustomization.isMeal || (mealCustomization.selectedSide && mealCustomization.selectedDrink)) {
                      const newItem: OrderItem = {
                        id: Date.now().toString(),
                        menu_item_id: mealCustomization.menuItem.id,
                        quantity: 1,
                        menu_item: mealCustomization.menuItem,
                        is_meal: mealCustomization.isMeal,
                        meal_side: mealCustomization.selectedSide,
                        meal_drink: mealCustomization.selectedDrink
                      }
                      setCart([...cart, newItem])
                      setShowMealCustomization(false)
                      setMealCustomization(null)
                    }
                  }}
                  disabled={mealCustomization.isMeal && (!mealCustomization.selectedSide || !mealCustomization.selectedDrink)}
                  className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  Add to Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Handheld
